/* Conversions into this season, above the episode grid: progress while a
   video (or a whole series) is being cut into episodes, and the result (or
   why it failed). Polls while anything is running and tells the page when
   new episodes landed. Preview opens the vertical player on what was made;
   Publish all publishes its draft episodes that are ready to play. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import um from "../../userManagement/userManagement.module.css";
import s from "./MicroDrama.module.css";
import { showToast } from "../../../utils/Toastify/Toastify";
import PreviewModal from "./PreviewModal";
import {
  IN_FLIGHT,
  listConversions,
  cancelConversion,
  retryConversion,
  publishConversion,
  groupConversions,
  apiErrorMessage,
} from "./microDramaService";

const POLL_MS = 5000;
const RECENT_MS = 24 * 3600 * 1000;

/** Running cards, plus anything that finished in the last day. */
export function visibleGroups(groups, now = Date.now()) {
  return (groups || []).filter((g) => {
    if (IN_FLIGHT.includes(g.status)) return true;
    const t = Date.parse(g.completed || g.created || "");
    return Number.isFinite(t) && now - t < RECENT_MS;
  });
}

/** Kept for callers/tests that work on plain conversion rows. */
export function visibleConversions(list, now = Date.now()) {
  return (list || []).filter((c) => {
    if (IN_FLIGHT.includes(c.status)) return true;
    const t = Date.parse(c.completed_at || c.created_at || "");
    return Number.isFinite(t) && now - t < RECENT_MS;
  });
}

export default function ConversionsPanel({ seriesId, seasonId, refreshKey = 0, canEdit = true, onEpisodesChanged }) {
  const [list, setList] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [preview, setPreview] = useState(null); // { id, title }
  const doneRef = useRef(null); // total episodes made, to notice new ones

  const load = useCallback(async () => {
    try {
      const rows = await listConversions(seriesId, seasonId);
      setList(rows);
      const made = rows.reduce((n, c) => n + (c.parts_done || 0), 0);
      if (doneRef.current !== null && made !== doneRef.current) onEpisodesChanged?.();
      doneRef.current = made;
    } catch {
      // The panel is secondary: a failed poll just keeps the last state.
    }
  }, [seriesId, seasonId, onEpisodesChanged]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const running = list.some((c) => IN_FLIGHT.includes(c.status));
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const act = async (g, fn, okText) => {
    setBusyKey(g.key);
    try {
      await fn(g.id);
      showToast(okText, "success");
      await load();
    } catch (err) {
      showToast(apiErrorMessage(err), "error");
    } finally {
      setBusyKey(null);
    }
  };

  const publishAll = async (g) => {
    setBusyKey(g.key);
    try {
      const res = await publishConversion(g.id);
      const d = res?.data || {};
      const waiting = d.processing ? ` ${d.processing} still processing — publish them once they're ready.` : "";
      showToast(`${res?.message || "Published"}.${waiting}`, d.published ? "success" : "info");
      onEpisodesChanged?.();
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't publish"), "error");
    } finally {
      setBusyKey(null);
    }
  };

  const shown = visibleGroups(groupConversions(list));
  if (!shown.length) return null;

  return (
    <div className={s.panel} aria-label="Video conversions">
      {shown.map((g) => {
        const inFlight = IN_FLIGHT.includes(g.status);
        const tone = g.status === "failed" ? s.barBad : g.status === "ready" ? s.barOk : g.status === "canceled" ? s.barMuted : "";
        const busy = busyKey === g.key;
        return (
          <div key={g.key} className={s.conv} data-testid="md-conversion">
            <div className={s.convMain}>
              <div className={s.convTitle}>
                <span className={s.convIcon} aria-hidden="true">✂</span>
                {g.title}
                <span className={s.muted}>
                  {" "}
                  · {g.reframe === "crop" ? "Fill the screen" : "Blurred background"} · ~{Math.round((g.target || 0) / 6) / 10} min episodes
                </span>
              </div>
              <div className={s.convStatus}>{g.text}</div>
              {g.status === "failed" && g.error && <div className={um.errorText}>{g.error}</div>}
              <div className={s.bar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={g.progress || 0}>
                <div className={`${s.barFill} ${tone} ${inFlight ? s.barLive : ""}`} style={{ width: `${Math.max(inFlight ? 3 : 0, g.progress || 0)}%` }} />
              </div>
            </div>
            <div className={s.convActions}>
              {g.partsDone > 0 && (
                <button type="button" className={um.btnGhost} onClick={() => setPreview({ id: g.id, title: g.title })}>
                  Preview
                </button>
              )}
              {canEdit && g.partsDone > 0 && (
                <button type="button" className={um.btnGhost} disabled={busy} onClick={() => publishAll(g)}>
                  Publish all
                </button>
              )}
              {canEdit && inFlight && (
                <button type="button" className={um.btnGhost} disabled={busy} onClick={() => act(g, cancelConversion, "Stopping after the current episode")}>
                  Stop
                </button>
              )}
              {canEdit && (g.status === "failed" || g.status === "canceled") && (
                <button type="button" className={um.btnGhost} disabled={busy} onClick={() => act(g, retryConversion, "Continuing where it stopped")}>
                  {g.status === "failed" ? "Retry" : "Resume"}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {preview && (
        <PreviewModal
          conversionId={preview.id}
          title={preview.title}
          canPublish={canEdit}
          onClose={() => setPreview(null)}
          onPublished={() => onEpisodesChanged?.()}
        />
      )}
    </div>
  );
}
